'use client'

import type { DerivationPaths } from '@polaris/core/services/server'
import { type FeatureFlags, featureFlagsAtom } from '@polaris/core/stores'
import { atomsGlobalStore } from '@polaris/core/utils'
import { onlineManager, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Provider, useAtom, useSetAtom } from 'jotai'
import { queryClientAtom } from 'jotai-tanstack-query'
import { usePathname } from 'next/navigation'
import { NuqsAdapter } from 'nuqs/adapters/next/app'
import { type PropsWithChildren, useEffect } from 'react'
import { toast } from 'sonner'
import type { DatadogConfig } from '@/src/configs/datadog'
import { getQueryClient } from '@/src/configs/tanstack-query'
import { useBackgroundActivation } from '@/src/hooks/account/use-background-activation'
import { useHotkeyGeneratedListener } from '@/src/hooks/hotkeys/use-hotkey-update'
import { useWalletKeyring } from '@/src/hooks/wallet/use-wallet-keyring'
import { useWalletOptions } from '@/src/hooks/wallet/use-wallet-options'
import {
  readyPendingGroupingKeyEffectAtom,
  showPendingAccountToastAtom,
  walletQueueDrainEffectAtom,
  walletStateSettleEffectAtom,
} from '@/src/stores/accounts/detection'
import { balancesStreamAtom } from '@/src/stores/balances/atom'
import { cosmosDerivationPathsAtom } from '@/src/stores/hotkeys/atoms'
import { mpcSideEffectsAtom } from '@/src/stores/mpc/effects'
import { useRouteMachineSubscriptions } from '@/src/stores/route-machine/actions'
import {
  walletKeyringAtom,
  walletOptionsAtom,
} from '@/src/stores/wallets/atoms'
import { DataDog } from '../datadog'
import { offlineToast } from '../ui/toast'
import { AmplitudeProvider } from './amplitude'
import { HydrateAtoms } from './atom-hydrate'
import { EcosystemProviders } from './ecosystems'
import type { EcosystemsServerContext } from './ecosystems-context'
import { ThemeProvider } from './theme-provider'

onlineManager.subscribe((isOnline) => {
  if (!isOnline) {
    offlineToast()
  } else {
    toast.dismiss('offline-toast')
  }
})

const WalletsAtomSubscription = () => {
  const walletKeyring = useWalletKeyring()
  const walletOptions = useWalletOptions()

  const setWalletKeyring = useSetAtom(walletKeyringAtom)
  const setWalletOptions = useSetAtom(walletOptionsAtom)
  useAtom(balancesStreamAtom)
  useAtom(mpcSideEffectsAtom)
  useAtom(showPendingAccountToastAtom)
  useAtom(walletStateSettleEffectAtom)
  useAtom(readyPendingGroupingKeyEffectAtom)
  useAtom(walletQueueDrainEffectAtom)

  useEffect(() => {
    // Update both wallet states atomically
    setWalletKeyring(walletKeyring)
    setWalletOptions(walletOptions)
  }, [walletKeyring, walletOptions, setWalletKeyring, setWalletOptions])

  return null
}

const RouteMachineSubscriptions = () => {
  useRouteMachineSubscriptions()

  return null
}

const BackgroundActivationSubscription = () => {
  useBackgroundActivation()
  return null
}

interface JotaiProviderProps {
  featureFlags: FeatureFlags
  cosmosDerivationPaths: DerivationPaths
}

const JotaiProvider = ({
  featureFlags,
  cosmosDerivationPaths,
  children,
}: PropsWithChildren<JotaiProviderProps>) => {
  const queryClient = getQueryClient()
  const walletKeyring = useWalletKeyring()
  const walletOptions = useWalletOptions()

  return (
    <Provider store={atomsGlobalStore}>
      <HydrateAtoms
        initialValues={[
          [queryClientAtom, queryClient],
          [walletKeyringAtom, walletKeyring],
          [walletOptionsAtom, walletOptions],
          [featureFlagsAtom, featureFlags],
          [cosmosDerivationPathsAtom, cosmosDerivationPaths],
        ]}
      >
        {children}
      </HydrateAtoms>
    </Provider>
  )
}

interface ProvidersProps extends JotaiProviderProps {
  /** User gave tracking consent */
  trackingConsent: boolean
  datadogConfig: DatadogConfig | undefined
  ecosystemsContext: EcosystemsServerContext
}

export const Providers = ({
  trackingConsent,
  datadogConfig,
  ecosystemsContext,
  featureFlags,
  cosmosDerivationPaths,
  children,
}: PropsWithChildren<ProvidersProps>) => {
  const pathname = usePathname()
  const queryClient = getQueryClient()
  useHotkeyGeneratedListener()

  const isLoginPage = pathname.startsWith('/login')

  // We use amplitude for offboarding page feedback collection
  const isOffboardingPage = pathname.startsWith('/offboarding')

  const collectAnalyticsData =
    // Users have not consented in landing page yet
    !isLoginPage &&
    // if we are in production we need to have user consent
    ((trackingConsent && process.env.VERCEL_ENV === 'production') ||
      // in __non_prod__ we can go ahead without consent
      // Note: we still have fine grained control over when to use what through env variable configuration
      process.env.VERCEL_ENV !== 'production')

  return (
    <NuqsAdapter>
      <QueryClientProvider client={queryClient}>
        <EcosystemProviders context={ecosystemsContext}>
          <JotaiProvider
            featureFlags={featureFlags}
            cosmosDerivationPaths={cosmosDerivationPaths}
          >
            <ThemeProvider
              attribute='class'
              defaultTheme='dark'
              enableSystem
              disableTransitionOnChange
              forcedTheme={featureFlags.lightMode ? undefined : 'dark'}
            >
              <AmplitudeProvider
                enabled={collectAnalyticsData || isOffboardingPage}
              >
                <RouteMachineSubscriptions />
                <WalletsAtomSubscription />
                <BackgroundActivationSubscription />
                {children}
                <ReactQueryDevtools
                  buttonPosition='bottom-left'
                  initialIsOpen={false}
                />
                {datadogConfig && collectAnalyticsData && (
                  <DataDog config={datadogConfig} />
                )}
              </AmplitudeProvider>
            </ThemeProvider>
          </JotaiProvider>
        </EcosystemProviders>
      </QueryClientProvider>
    </NuqsAdapter>
  )
}
